import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Avatar from '../components/Avatar';
import Icon from '../components/Icon';
import { Alert, BackButton, BotBadge, LoadingBlock } from '../components/ui';
import { api } from '../lib/api';
import { clockTime, dayKey, dayLabel, presenceLabel } from '../lib/format';

const BOT_POLL_MS = 3000;
const HUMAN_POLL_MS = 8000;
const GROUP_GAP_MS = 5 * 60 * 1000;
const NEAR_BOTTOM_PX = 140;

export default function ChatScreen({ me, recipient, token, onBack, onOpenProfile }) {
  const [messages, setMessages] = useState([]);
  const [outbox, setOutbox] = useState([]);
  const [draft, setDraft] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [botTyping, setBotTyping] = useState(false);

  const scrollRef = useRef(null);
  const textareaRef = useRef(null);
  const sendingCount = useRef(0);
  const stickToBottom = useRef(true);

  const senderIdOf = (msg) => String(msg.sender?._id || msg.sender);
  const isMine = useCallback((msg) => senderIdOf(msg) === String(me.id), [me.id]);

  const loadMessages = useCallback(
    async ({ silent = false } = {}) => {
      try {
        const data = await api.get(`/api/chat/${recipient._id}`, token);
        // A poll that lands mid-send would drop the optimistic bubble, so skip it.
        if (sendingCount.current === 0) setMessages(Array.isArray(data) ? data : []);
        setLoadError('');
      } catch (err) {
        if (!silent) setLoadError(err.message);
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [recipient._id, token]
  );

  useEffect(() => {
    setLoading(true);
    setMessages([]);
    setOutbox([]);
    stickToBottom.current = true;
    loadMessages();
  }, [loadMessages]);

  useEffect(() => {
    const interval = recipient.isBot ? BOT_POLL_MS : HUMAN_POLL_MS;
    const id = window.setInterval(() => {
      if (document.visibilityState === 'visible') loadMessages({ silent: true });
    }, interval);
    return () => window.clearInterval(id);
  }, [loadMessages, recipient.isBot]);

  const thread = useMemo(() => [...messages, ...outbox], [messages, outbox]);

  useEffect(() => {
    if (loading) return;
    const el = scrollRef.current;
    if (!el) return;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < NEAR_BOTTOM_PX;
    if (stickToBottom.current || nearBottom) {
      el.scrollTop = el.scrollHeight;
      stickToBottom.current = false;
    }
  }, [thread.length, botTyping, loading]);

  const resizeTextarea = () => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 130)}px`;
  };

  const deliver = async (entry) => {
    sendingCount.current += 1;
    if (recipient.isBot) setBotTyping(true);
    try {
      const data = await api.post(`/api/chat/${recipient._id}`, { content: entry.content }, token);
      const created = [data?.message, data?.botReply].filter(Boolean);
      setMessages((prev) => [...prev, ...(created.length ? created : [{ ...entry, _id: entry.localId }])]);
      setOutbox((prev) => prev.filter((m) => m.localId !== entry.localId));
    } catch (err) {
      setOutbox((prev) =>
        prev.map((m) => (m.localId === entry.localId ? { ...m, failed: true, error: err.message } : m))
      );
    } finally {
      sendingCount.current -= 1;
      setBotTyping(false);
    }
  };

  const send = (e) => {
    e?.preventDefault();
    const content = draft.trim();
    if (!content) return;
    const entry = {
      localId: `local-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      sender: me.id,
      receiver: recipient._id,
      content,
      timestamp: new Date().toISOString(),
      pending: true,
    };
    setDraft('');
    setOutbox((prev) => [...prev, entry]);
    stickToBottom.current = true;
    window.requestAnimationFrame(resizeTextarea);
    deliver(entry);
  };

  const retry = (entry) => {
    setOutbox((prev) =>
      prev.map((m) => (m.localId === entry.localId ? { ...m, failed: false, error: undefined } : m))
    );
    deliver(entry);
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <div className="chat-screen">
      <header className="topbar">
        <div className="topbar-inner">
          <BackButton onClick={onBack} />
          <button
            type="button"
            onClick={() => onOpenProfile(recipient)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 11,
              flex: 1,
              minWidth: 0,
              background: 'none',
              border: 'none',
              padding: 0,
              textAlign: 'left',
            }}
          >
            <Avatar user={recipient} size={40} showPresence />
            <span className="topbar-title">
              <span style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {recipient.username}
                </span>
                {recipient.isBot && <BotBadge />}
              </span>
              <span className="topbar-subtitle">
                {botTyping ? 'typing…' : presenceLabel(recipient.lastSeen)}
              </span>
            </span>
          </button>
        </div>
      </header>

      <div className="chat-scroll" ref={scrollRef}>
        {loading ? (
          <LoadingBlock text="Loading conversation…" />
        ) : (
          <div className="chat-thread">
            {loadError && (
              <div style={{ marginBottom: 12 }}>
                <Alert kind="error">{loadError}</Alert>
              </div>
            )}

            {thread.length === 0 && !loadError && (
              <div style={{ textAlign: 'center', padding: '48px 12px', color: 'var(--text-dim)' }}>
                <div style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--text)', marginBottom: 6 }}>
                  Say hi to {recipient.username}
                </div>
                <p style={{ fontSize: '0.9rem', lineHeight: 1.6 }}>
                  This is the start of your conversation. Open with something better than “hey”.
                </p>
              </div>
            )}

            {thread.map((msg, index) => {
              const previous = thread[index - 1];
              const mine = isMine(msg);
              const newDay = !previous || dayKey(previous.timestamp) !== dayKey(msg.timestamp);
              const startOfGroup =
                newDay ||
                isMine(previous) !== mine ||
                new Date(msg.timestamp) - new Date(previous.timestamp) > GROUP_GAP_MS;

              return (
                <React.Fragment key={msg._id || msg.localId}>
                  {newDay && <div className="chat-day">{dayLabel(msg.timestamp)}</div>}
                  <div className={`bubble-row${mine ? ' bubble-row-own' : ''}${startOfGroup ? ' bubble-row-start' : ''}`}>
                    <div
                      className={[
                        'bubble',
                        mine ? 'bubble-own' : '',
                        startOfGroup ? 'bubble-start' : '',
                        msg.pending && !msg.failed ? 'bubble-pending' : '',
                        msg.failed ? 'bubble-failed' : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                    >
                      {msg.content}
                      <div className="bubble-meta">
                        {msg.failed ? (
                          <>
                            <Icon name="alert" size={12} />
                            <span>Not sent</span>
                            <button type="button" className="bubble-retry" onClick={() => retry(msg)}>
                              Retry
                            </button>
                          </>
                        ) : (
                          <span>{msg.pending ? 'Sending…' : clockTime(msg.timestamp)}</span>
                        )}
                      </div>
                    </div>
                  </div>
                </React.Fragment>
              );
            })}

            {botTyping && (
              <div className="bubble-row bubble-row-start">
                <div className="bubble typing" aria-label={`${recipient.username} is typing`}>
                  <span />
                  <span />
                  <span />
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <form className="composer" onSubmit={send}>
        <div className="composer-inner">
          <textarea
            ref={textareaRef}
            className="composer-input"
            rows={1}
            value={draft}
            onChange={(e) => {
              setDraft(e.target.value);
              resizeTextarea();
            }}
            onKeyDown={onKeyDown}
            placeholder={`Message ${recipient.username}…`}
            aria-label="Write a message"
          />
          <button type="submit" className="composer-send" disabled={!draft.trim()} aria-label="Send message">
            <Icon name="send" size={19} />
          </button>
        </div>
      </form>
    </div>
  );
}
